import { useState } from 'react';
import { CaretDownIcon, CaretRightIcon, FolderIcon, FolderOpenIcon } from '@phosphor-icons/react';

import { type FileEntry, type FileTreeNode, listDirectory } from '@/api-models/sandbox-files';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/ui/ContextMenu';
import classNames from '@/utils/classnames';

import { useSelectionStore } from '../../../stores/selection-store';

import { getFileIcon } from './file-icons';

interface FileTreeProps {
  base: string;
  nodes: FileTreeNode[];
  selectedPath: string | null;
  onSelect: (path: string) => void;
  onPreview: (path: string) => void;
}

function entriesToNodes(parentPath: string, entries: FileEntry[]): FileTreeNode[] {
  const dirs = entries
    .filter((e) => e.is_dir)
    .map((e) => ({ name: e.name, path: `${parentPath}/${e.name}`, is_dir: true, lazy: true }));
  const files = entries
    .filter((e) => !e.is_dir)
    .map((e) => ({ name: e.name, path: `${parentPath}/${e.name}`, is_dir: false }));
  return [...dirs, ...files];
}

/** What every row of the tree reads: the tree's own expansion state and its callbacks. */
interface TreeState {
  expanded: Set<string>;
  lazyChildren: Map<string, FileTreeNode[]>;
  loadingPaths: Set<string>;
  selectedPath: string | null;
  onToggle: (node: FileTreeNode) => void;
  onSelect: (path: string) => void;
  onPreview: (path: string) => void;
  onAttach: (path: string) => void;
}

/** One node and, when it is an expanded directory, its children — recursively. */
function FileTreeItem({ node, depth, tree }: { node: FileTreeNode; depth: number; tree: TreeState }) {
  const isExpanded = tree.expanded.has(node.path);
  const children = node.lazy ? tree.lazyChildren.get(node.path) : node.children;
  const isLoading = tree.loadingPaths.has(node.path);
  const isMarkdown = node.name.toLowerCase().endsWith('.md');
  const FileTypeIcon = node.is_dir ? null : getFileIcon(node.name);

  const row = (
    <button
      type="button"
      onClick={() => (node.is_dir ? tree.onToggle(node) : tree.onSelect(node.path))}
      className={classNames(
        'flex h-6.5 w-full items-center gap-1.5 rounded-md px-2 text-left text-xs hover:bg-surface-3',
        tree.selectedPath === node.path && 'bg-brand-tint text-brand-text',
      )}
      style={{ paddingLeft: `${depth * 14 + 8}px` }}
    >
      {node.is_dir ? (
        <>
          {isExpanded ? (
            <CaretDownIcon size={11} className="shrink-0 text-fg-tertiary" />
          ) : (
            <CaretRightIcon size={11} className="shrink-0 text-fg-tertiary" />
          )}
          {isExpanded ? (
            <FolderOpenIcon size={14} className="shrink-0 text-brand-text" />
          ) : (
            <FolderIcon size={14} className="shrink-0 text-brand-text" />
          )}
        </>
      ) : (
        <>
          <span className="w-2.75 shrink-0" />
          {FileTypeIcon && <FileTypeIcon size={14} className="shrink-0 text-fg-tertiary" />}
        </>
      )}
      <span
        className={classNames('truncate', tree.selectedPath === node.path ? 'text-brand-text' : 'text-fg-secondary')}
      >
        {node.name}
      </span>
    </button>
  );

  return (
    <div>
      {node.is_dir ? (
        row
      ) : (
        <ContextMenu>
          <ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem onSelect={() => tree.onAttach(node.path)}>Add to chat</ContextMenuItem>
            {isMarkdown && (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem onSelect={() => tree.onPreview(node.path)}>Preview</ContextMenuItem>
              </>
            )}
          </ContextMenuContent>
        </ContextMenu>
      )}
      {node.is_dir && isExpanded && (
        <div>
          {isLoading && (
            <div className="px-3 py-1 text-2xs text-fg-tertiary" style={{ paddingLeft: `${(depth + 1) * 14 + 8}px` }}>
              Loading…
            </div>
          )}
          {children?.map((child) => (
            <FileTreeItem key={child.path} node={child} depth={depth + 1} tree={tree} />
          ))}
        </div>
      )}
    </div>
  );
}

export function FileTree({ base, nodes, selectedPath, onSelect, onPreview }: FileTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [lazyChildren, setLazyChildren] = useState<Map<string, FileTreeNode[]>>(new Map());
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());
  const addFile = useSelectionStore((s) => s.addFile);

  const attachFile = (path: string) => addFile({ path });

  const toggle = async (node: FileTreeNode) => {
    const isExpanded = expanded.has(node.path);
    if (isExpanded) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(node.path);
        return next;
      });
      return;
    }

    setExpanded((prev) => new Set(prev).add(node.path));

    if (node.lazy && !lazyChildren.has(node.path)) {
      setLoadingPaths((prev) => new Set(prev).add(node.path));
      try {
        const entries = await listDirectory(base, node.path);
        setLazyChildren((prev) => new Map(prev).set(node.path, entriesToNodes(node.path, entries)));
      } finally {
        setLoadingPaths((prev) => {
          const next = new Set(prev);
          next.delete(node.path);
          return next;
        });
      }
    }
  };

  const tree: TreeState = {
    expanded,
    lazyChildren,
    loadingPaths,
    selectedPath,
    onToggle: (node) => void toggle(node),
    onSelect,
    onPreview,
    onAttach: attachFile,
  };

  return (
    <div className="flex flex-col gap-0.5 overflow-y-auto p-1.5">
      {nodes.map((node) => (
        <FileTreeItem key={node.path} node={node} depth={0} tree={tree} />
      ))}
    </div>
  );
}
